/* eslint-disable @typescript-eslint/naming-convention */
import { ID } from '@datorama/akita';
import { Observable } from 'rxjs';

import { Entity, OneToOne } from '../entity';
import { PageTitles } from '../interfaces/types';
import { IEntity } from '../interfaces/models';
import { Walklist } from './walklist.model';
// never import from '../services'
import { WalklistService } from '../services/walklist';
import { Result } from '../interfaces/helpers';
import { formatAddressString } from '../lib/utils';

export interface IAddressExtensionRaw {
  city: string;
  coid: string;
  created_at: Date;
  created_by: string;
  derived_drop_permission_status: string;
  dir_prefix: string;
  dir_suffix: string;
  do_not_knock: boolean;
  dp_walklist_address_seq: number;
  dp_walklist_id: number;
  floor: string;
  fsa: string;
  is_deleted: boolean;
  language: string;
  last_knock_status: string;
  modified_at: Date;
  modified_by: string;
  outcome_id: string;
  postal_code: string;
  premise_type: string;
  province: string;
  sales_premise_type_rco_jira: string;
  sales_walklist_address_seq: number;
  sales_walklist_id: number;
  sayb_d2c_decision: string;
  street_name: string;
  street_number: string;
  street_number_prefix: string;
  street_number_suffix: string;
  street_type_prefix: string;
  street_type_suffix: string;
  unit: string;
  formatted_street_number: number;
  formatted_street_name: string;
  existing_product: {
    MOB: number;
  };
  product_availability: {
    LWC: number;
    PIK: number;
    SHS: number;
    STV: number;
    TOS: number;
    TTV: number;
    HSIC: number;
    SING: number;
  };
  contract_status?: string;
  current_technology_type: {
    M: number;
  };
  available_technology_type: {
    C: number;
    F: number;
    M: number;
    S: number;
  };
  sa_units?: string;
  first_nations: boolean;
  drop_status?: string;
  demographics?: string;
  copper_port_availability?: string;
  internet_speed: number;
  tier: number;
  current_lt_value: number;
  current_download_speed?: string;
}

export interface IAddressExtension extends IEntity {
  addressId: string;
  derivedDpStatus: string;
  doNotKnock: boolean;
  dpWalklistAddressSeq: number;
  dpWalklistId?: number;
  language: string;
  lastKnockStatus: string;
  salesPremiseTypeRcoJira: string;
  salesWalklistAddressSeq: number;
  salesWalklistId?: number;
  addressGeom?: any;
  unit?: string;
  floor?: string;
  streetNumberPrefix?: string;
  streetNumber?: string;
  streetNumberSuffix?: string;
  dirPrefix?: string;
  streetTypePrefix?: string;
  streetName?: string;
  streetTypeSuffix?: string;
  dirSuffix?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  existingBusinessProducts?: string;
  dropWalklist?: Observable<Result<Walklist>>;
  salesWalklist?: Observable<Result<Walklist>>;
  walklistKnockIds?: number[] | null;
}
// IEntity contains the required metadata createdBy etc
export interface ITerritoryAddress {
  addressId: string;
  addressGeom?: {
    type: string;
    coordinates: any;
  };
  territoryId: number;
}

@Entity({
  name: PageTitles.addressActivity,
})
export class Address implements IAddressExtension {
  @OneToOne({
    repository: WalklistService,
    api: (i: Address) => (i.dpWalklistId ? `/${i.dpWalklistId}` : ''),
  })
  dropWalklist: Observable<Result<Walklist>>;

  @OneToOne({
    repository: WalklistService,
    api: (i: Address) => (i.salesWalklistId ? `/${i.salesWalklistId}` : ''),
  })
  salesWalklist: Observable<Result<Walklist>>;

  id: ID;
  addressId: string;
  derivedDpStatus: string;
  doNotKnock: boolean;
  dpWalklistAddressSeq: number;
  dpWalklistId?: number;
  language: string;
  lastKnockStatus: string;
  salesPremiseTypeRcoJira: string;
  salesWalklistAddressSeq: number;
  salesWalklistId?: number;
  createdAt?: Date;
  modifiedAt?: Date;
  createdBy?: string;
  modifiedBy?: string;
  href?: string;
  addressGeom?: any;
  unit?: string;
  floor?: string;
  streetNumberPrefix?: string;
  streetNumber?: string;
  streetNumberSuffix?: string;
  dirPrefix?: string;
  streetTypePrefix?: string;
  streetName?: string;
  streetTypeSuffix?: string;
  dirSuffix?: string;
  city?: string;
  province?: string;
  postalCode?: string;

  constructor(
    address: IAddressExtension = {
      id: '',
      addressId: '',
      derivedDpStatus: '',
      doNotKnock: false,
      dpWalklistAddressSeq: 0,
      dpWalklistId: null,
      language: '',
      lastKnockStatus: '',
      salesPremiseTypeRcoJira: '',
      salesWalklistAddressSeq: 0,
      salesWalklistId: null,
      href: '',
      createdBy: '',
      createdAt: new Date(),
      modifiedBy: '',
      modifiedAt: new Date(),
      addressGeom: '',
      unit: '',
      floor: '',
      streetNumberPrefix: '',
      streetNumber: '',
      streetNumberSuffix: '',
      dirPrefix: '',
      streetTypePrefix: '',
      streetName: '',
      streetTypeSuffix: '',
      dirSuffix: '',
      city: '',
      province: '',
      postalCode: '',
    }
  ) {
    const {
      id,
      addressId,
      derivedDpStatus,
      doNotKnock,
      dpWalklistAddressSeq,
      dpWalklistId,
      language,
      lastKnockStatus,
      salesPremiseTypeRcoJira,
      salesWalklistAddressSeq,
      salesWalklistId,
      href,
      createdBy,
      createdAt,
      modifiedBy,
      modifiedAt,
      addressGeom,
      unit,
      floor,
      streetNumberPrefix,
      streetNumber,
      streetNumberSuffix,
      dirPrefix,
      streetTypePrefix,
      streetName,
      streetTypeSuffix,
      dirSuffix,
      city,
      province,
      postalCode,
    } = address;

    this.id = id;
    this.addressId = addressId;
    this.derivedDpStatus = derivedDpStatus;
    this.doNotKnock = doNotKnock;
    this.dpWalklistAddressSeq = dpWalklistAddressSeq;
    this.dpWalklistId = dpWalklistId;
    this.language = language;
    this.lastKnockStatus = lastKnockStatus;
    this.salesPremiseTypeRcoJira = salesPremiseTypeRcoJira;
    this.salesWalklistAddressSeq = salesWalklistAddressSeq;
    this.salesWalklistId = salesWalklistId;
    this.href = href;
    this.createdBy = createdBy;
    this.createdAt = createdAt;
    this.modifiedBy = modifiedBy;
    this.modifiedAt = modifiedAt;
    this.addressGeom = addressGeom;
    this.unit = unit;
    this.floor = floor;
    this.streetNumberPrefix = streetNumberPrefix;
    this.streetNumber = streetNumber;
    this.streetNumberSuffix = streetNumberSuffix;
    this.dirPrefix = dirPrefix;
    this.streetTypePrefix = streetTypePrefix;
    this.streetName = streetName;
    this.streetTypeSuffix = streetTypeSuffix;
    this.dirSuffix = dirSuffix;
    this.city = city;
    this.province = province;
    this.postalCode = postalCode;
  }

  // Since ion datepicker receives a string in iso 8601
  // and returns a string, I created this property to manage that
  get createdAtFormatted(): string {
    return this.createdAt.toISOString();
  }

  set createdAtFormatted(date: string) {
    this.createdAt = new Date(date);
  }

  get addressString(): string {
    return formatAddressString(this);
  }
}
